import axios, { AxiosInstance } from "axios";
import {
  User,
  Company,
  Candidate,
  Resume,
  LoginRequest,
  RegisterRequest,
  TokenResponse,
  CandidateSearchRequest,
  CandidateSearchResponse,
  CandidateAnalytics,
  CandidateProfileAnalytics,
  FileUploadResponse,
  SuperAdminStats,
  JobDescriptionSearch,
  Job,
  ResumeStatus,
} from "../types";
import { getErrorMessage } from "../utils/errorMessages";

export interface SuperAdminUser {
  id: string;
  email: string;
  first_name: string;
  last_name: string;
  super_admin_role: string;
  is_active: boolean;
  is_verified: boolean;
  created_at: string;
  last_login?: string | null;
  updated_at?: string;
}

export interface SuperAdminUsersResponse {
  users: SuperAdminUser[];
  totalUsers: number;
  activeUsers: number;
  verifiedUsers: number;
  roleBreakdown: Record<string, number>;
}

export interface AdminTrialConfig {
  message?: string;
  default_trial_days: number | string;
  default_trial_credits: number | string;
  trial_topup_enabled: boolean | string;
}

export interface AdminTrialConfigUpdateRequest {
  trial_days?: number;
  trial_credits?: number;
  topup_enabled?: boolean;
}

export interface AdminCreditActionsQuery {
  search?: string;
  offset?: number;
  limit?: number;
}

class ApiService {
  private api: AxiosInstance;
  public baseURL: string;
  private isRefreshing: boolean = false;
  private failedQueue: Array<{ resolve: Function; reject: Function }> = [];

  constructor() {
    this.baseURL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

    this.api = axios.create({
      baseURL: `${this.baseURL}/api/v1`,
      headers: {
        "Content-Type": "application/json",
      },
    });

    // Request interceptor to add auth token
    this.api.interceptors.request.use(
      (config) => {
        const token = localStorage.getItem("access_token");
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
      },
      (error) => Promise.reject(error),
    );

    // Response interceptor to handle token refresh and error messages
    this.api.interceptors.response.use(
      (response) => response,
      async (error) => {
        const originalRequest = error.config;

        // Handle 401 errors for token refresh
        if (error.response?.status === 401 && !originalRequest._retry) {
          // Skip automatic handling for login endpoints - let them handle their own errors
          if (
            originalRequest.url &&
            (originalRequest.url.includes("/auth/login") ||
              originalRequest.url.includes("/auth/company-user/login") ||
              originalRequest.url.includes("/auth/register") ||
              originalRequest.url.includes("/auth/company-user/register"))
          ) {
            return Promise.reject(error);
          }

          // Prevent infinite loop: if this is a refresh attempt, logout and redirect
          if (
            originalRequest.url &&
            originalRequest.url.includes("/auth/refresh")
          ) {
            console.error("🔄 Token refresh failed, logging out");
            this.clearAllAuthData();
            window.location.href = "/auth/login";
            return Promise.reject(error);
          }

          // Mark this request as retried
          originalRequest._retry = true;

          // If we're already refreshing, queue this request
          if (this.isRefreshing) {
            console.log("🔄 Already refreshing token, queueing request");
            return new Promise((resolve, reject) => {
              this.failedQueue.push({ resolve, reject });
            })
              .then((token) => {
                originalRequest.headers.Authorization = `Bearer ${token}`;
                return this.api(originalRequest);
              })
              .catch((err) => {
                return Promise.reject(err);
              });
          }

          const refreshToken = localStorage.getItem("refresh_token");
          if (refreshToken) {
            console.log("🔄 Starting token refresh...");
            this.isRefreshing = true;

            try {
              const response = await this.refreshToken(refreshToken);
              console.log("✅ Token refresh successful");
              localStorage.setItem("access_token", response.access_token);
              localStorage.setItem("refresh_token", response.refresh_token);

              // Process the failed queue
              this.processQueue(null, response.access_token);

              // Retry the original request
              originalRequest.headers.Authorization = `Bearer ${response.access_token}`;
              console.log("🔄 Retrying original request with new token");
              return this.api(originalRequest);
            } catch (refreshError) {
              console.error("❌ Token refresh failed:", refreshError);
              this.processQueue(refreshError, null);
              this.clearAllAuthData();
              window.location.href = "/auth/login";
              return Promise.reject(refreshError);
            } finally {
              this.isRefreshing = false;
            }
          } else {
            console.error("❌ No refresh token available, logging out");
            this.clearAllAuthData();
            window.location.href = "/auth/login";
            return Promise.reject(error);
          }
        }

        // Handle other HTTP errors by extracting the detailed error message
        if (error.response?.data?.detail) {
          // FastAPI returns error messages in the 'detail' field
          const message = getErrorMessage(
            error.response.data.detail,
            "Something went wrong. Please try again.",
          );
          const customError: any = new Error(message);
          customError.name = "ApiError";
          customError.response = error.response;
          customError.status = error.response.status;
          return Promise.reject(customError);
        } else if (error.response?.data?.message) {
          // Some APIs might use 'message' field
          const message = getErrorMessage(
            error.response.data.message,
            "Something went wrong. Please try again.",
          );
          const customError: any = new Error(message);
          customError.name = "ApiError";
          customError.response = error.response;
          customError.status = error.response.status;
          return Promise.reject(customError);
        }

        return Promise.reject(error);
      },
    );
  }

  private processQueue(error: any, token: string | null) {
    this.failedQueue.forEach(({ resolve, reject }) => {
      if (error) {
        reject(error);
      } else {
        resolve(token);
      }
    });

    this.failedQueue = [];
  }
  public get(url: string, params?: any) {
    return this.api.get(url, { params });
  }

  public post(url: string, data?: any) {
    return this.api.post(url, data);
  }

  public put(url: string, data?: any) {
    return this.api.put(url, data);
  }

  public delete(url: string) {
    return this.api.delete(url);
  }
  public patch(url: string, data?: any) {
    return this.api.patch(url, data);
  }
  // Authentication Methods
  async login(credentials: LoginRequest): Promise<TokenResponse> {
    // Check if this is a super admin email (TalentKey.AI domain)
    if (credentials.email.toLowerCase().includes("@talentkeyai.com")) {
      try {
        console.log("🔧 Trying super admin login for:", credentials.email);
        const response = await this.api.post<TokenResponse>(
          "/auth/super-admin/login",
          credentials,
        );
        const { access_token, refresh_token, user } = response.data;
        const userWithPersona = user
          ? { ...user, persona: response.data.persona || user.persona }
          : user;

        localStorage.setItem("access_token", access_token);
        localStorage.setItem("refresh_token", refresh_token);
        if (userWithPersona) {
          localStorage.setItem("user", JSON.stringify(userWithPersona));
        }

        console.log("✅ Super admin login successful");
        return response.data;
      } catch (superAdminError: any) {
        console.log("❌ Super admin login failed, trying fallback");
        // Fall through to regular login attempts
      }
    }

    // Try company user login first (most common for new signups)
    try {
      const response = await this.api.post<TokenResponse>(
        "/auth/company-user/login",
        credentials,
      );
      const { access_token, refresh_token, user } = response.data;
      const userWithPersona = user
        ? { ...user, persona: response.data.persona || user.persona }
        : user;

      localStorage.setItem("access_token", access_token);
      localStorage.setItem("refresh_token", refresh_token);
      if (userWithPersona) {
        localStorage.setItem("user", JSON.stringify(userWithPersona));
      }

      return response.data;
    } catch (companyUserError: any) {
      // If company user login fails, try job seeker login
      if (companyUserError.response?.status === 401) {
        try {
          const response = await this.api.post<TokenResponse>(
            "/auth/job-seeker/login",
            credentials,
          );
          const { access_token, refresh_token, user } = response.data;
          const userWithPersona = user
            ? { ...user, persona: response.data.persona || user.persona }
            : user;

          localStorage.setItem("access_token", access_token);
          localStorage.setItem("refresh_token", refresh_token);
          if (userWithPersona) {
            localStorage.setItem("user", JSON.stringify(userWithPersona));
          }

          return response.data;
        } catch (jobSeekerError: any) {
          // If both fail, throw the more specific error
          if (jobSeekerError.response?.status === 401) {
            throw new Error("Incorrect email or password");
          }
          throw jobSeekerError;
        }
      }
      // If it's not a 401 error, throw the original error
      throw companyUserError;
    }
  }

  async register(userData: RegisterRequest): Promise<TokenResponse> {
    // Use company user registration as the default for register method
    const response = await this.api.post<TokenResponse>(
      "/auth/company-user/register",
      userData,
    );
    const { access_token, refresh_token } = response.data;

    localStorage.setItem("access_token", access_token);
    localStorage.setItem("refresh_token", refresh_token);

    // Fetch user data after successful registration
    try {
      const user = await this.getCurrentUser();
      localStorage.setItem("user", JSON.stringify(user));
      return { ...response.data, user };
    } catch (error) {
      // If user fetch fails, still return the token response
      console.warn("Failed to fetch user data after registration:", error);
      return response.data;
    }
  }

  async registerJobSeeker(userData: {
    email: string;
    first_name: string;
    last_name: string;
    country_code?: string;
    phone?: string;
    location?: string;
    country?: string;
    state?: string;
    city?: string;
  }): Promise<{ message: string; email: string; next_step: string }> {
    const response = await this.api.post("/auth/job-seeker/register", userData);
    return response.data;
  }

  async registerCompanyUser(userData: {
    email: string;
    first_name: string;
    last_name: string;
    company_name: string;
    country_code?: string;
    phone?: string;
    job_title?: string;
    company_website?: string;
    company_industry?: string;
    company_size?: string;
    subscription_tier?: string;
    location?: string;
    country?: string;
    state?: string;
    city?: string;
  }): Promise<{ message: string; email: string; next_step: string }> {
    const response = await this.api.post(
      "/auth/company-user/register",
      userData,
    );
    return response.data;
  }

  async refreshToken(refreshToken: string): Promise<TokenResponse> {
    const response = await this.api.post<TokenResponse>("/auth/refresh", {
      refresh_token: refreshToken,
    });
    return response.data;
  }

  async getCurrentUser(): Promise<User> {
    const response = await this.api.get<User>("/auth/me");
    return response.data;
  }

  async forgotPassword(email: string): Promise<any> {
    const response = await this.api.post("/auth/forgot-password", { email });
    return response.data;
  }

  async resetPassword(
    token: string,
    newPassword: string,
    confirmPassword: string,
  ): Promise<any> {
    const response = await this.api.post("/auth/reset-password", {
      token,
      new_password: newPassword,
      confirm_password: confirmPassword,
    });
    return response.data;
  }

  logout(): void {
    console.log("🔌 API Service: Starting comprehensive logout cleanup...");

    // Get current user to determine persona before clearing
    const currentUser = this.getCurrentUserFromStorage();
    const isJobSeeker =
      currentUser && !currentUser.company_id && !currentUser.is_super_admin;
    const isCompanyUser =
      currentUser && currentUser.company_id && !currentUser.is_super_admin;
    const isSuperAdminUser = currentUser && currentUser.is_super_admin;

    console.log("🔌 API Service persona cleanup for:", {
      isJobSeeker,
      isCompanyUser,
      isSuperAdmin: isSuperAdminUser,
    });

    // AGGRESSIVE CLEANUP: Clear ALL localStorage items that might be related
    console.log("🧹 Starting aggressive localStorage cleanup...");

    // Get all localStorage keys first
    const allLocalStorageKeys = Object.keys(localStorage);
    console.log(
      `🔍 Found ${allLocalStorageKeys.length} localStorage items:`,
      allLocalStorageKeys,
    );

    // Clear ALL localStorage items (most aggressive approach)
    localStorage.clear();
    console.log("✅ All localStorage cleared");

    // AGGRESSIVE CLEANUP: Clear ALL sessionStorage items
    console.log("🧹 Starting aggressive sessionStorage cleanup...");

    // Get all sessionStorage keys first
    const allSessionStorageKeys = Object.keys(sessionStorage);
    console.log(
      `🔍 Found ${allSessionStorageKeys.length} sessionStorage items:`,
      allSessionStorageKeys,
    );

    // Clear ALL sessionStorage items
    sessionStorage.clear();
    console.log("✅ All sessionStorage cleared");

    // Clear IndexedDB databases
    if ("indexedDB" in window) {
      console.log("🗃️ Clearing IndexedDB databases...");

      // Common IndexedDB database names that might be used
      const commonDBNames = [
        "tabee-app",
        "talentkeyai-app",
        "candidate-data",
        "company-data",
        "user-data",
        "auth-data",
        "cache-data",
        "offline-data",
        "workbox-precache",
        "workbox-runtime",
        "keyval-store",
      ];

      commonDBNames.forEach((dbName) => {
        try {
          const deleteReq = indexedDB.deleteDatabase(dbName);
          deleteReq.onsuccess = () =>
            console.log(`✅ Deleted IndexedDB: ${dbName}`);
          deleteReq.onerror = () =>
            console.log(`⚠️ Could not delete IndexedDB: ${dbName}`);
        } catch (error) {
          console.log(`⚠️ Error deleting IndexedDB ${dbName}:`, error);
        }
      });
    }

    // Clear Web SQL (if supported - deprecated but might exist)
    if ("webkitStorageInfo" in window || "storageInfo" in navigator) {
      console.log("🗃️ Attempting to clear Web SQL...");
      try {
        if ((window as any).openDatabase) {
          // This is deprecated but some browsers might still have data
          console.log("⚠️ Web SQL detected - manual cleanup may be needed");
        }
      } catch (error) {
        console.log("⚠️ Web SQL cleanup error:", error);
      }
    }

    // Clear Service Worker caches
    if ("caches" in window) {
      console.log("🗂️ Clearing Service Worker caches...");
      caches
        .keys()
        .then((cacheNames) => {
          console.log(`🔍 Found ${cacheNames.length} caches:`, cacheNames);
          return Promise.all(
            cacheNames.map((cacheName) => {
              console.log(`🗑️ Deleting cache: ${cacheName}`);
              return caches.delete(cacheName);
            }),
          );
        })
        .then(() => {
          console.log("✅ All Service Worker caches cleared");
        })
        .catch((error) => {
          console.log("⚠️ Error clearing caches:", error);
        });
    }

    // Clear cookies (more aggressive approach)
    console.log("🍪 Clearing cookies...");
    document.cookie.split(";").forEach((cookie) => {
      const eqPos = cookie.indexOf("=");
      const name = eqPos > -1 ? cookie.substr(0, eqPos).trim() : cookie.trim();

      // Clear for current domain
      document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/`;
      document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/;domain=${window.location.hostname}`;
      document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/;domain=.${window.location.hostname}`;

      // Also try clearing for localhost variants
      if (window.location.hostname === "localhost") {
        document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/;domain=localhost`;
        document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/;domain=.localhost`;
      }
    });
    console.log("✅ Cookies cleared");

    // Reset API service internal state
    this.isRefreshing = false;
    this.failedQueue = [];

    // Clear axios default headers if they exist
    if (this.api.defaults.headers.Authorization) {
      delete this.api.defaults.headers.Authorization;
    }

    // Clear any remaining memory references
    if (this.api.defaults.headers.common) {
      delete this.api.defaults.headers.common["Authorization"];
    }

    console.log("✅ API Service: AGGRESSIVE logout cleanup completed");
    console.log("🔍 Verifying cleanup...");

    // Verify cleanup
    setTimeout(() => {
      const remainingLocalStorage = Object.keys(localStorage).length;
      const remainingSessionStorage = Object.keys(sessionStorage).length;

      console.log("📊 Cleanup verification:", {
        localStorageItems: remainingLocalStorage,
        sessionStorageItems: remainingSessionStorage,
        authToken: localStorage.getItem("access_token"),
        user: localStorage.getItem("user"),
        company: localStorage.getItem("company"),
      });

      if (remainingLocalStorage > 0 || remainingSessionStorage > 0) {
        console.warn(
          "⚠️ Some storage items may still remain. Manual browser data clearing may be needed.",
        );
      } else {
        console.log("✅ All storage successfully cleared!");
      }
    }, 100);
  }

  clearAllAuthData(): void {
    console.log(
      "🧹 API Service: Clearing all auth data due to token refresh failure...",
    );

    // Use the comprehensive logout method
    this.logout();

    console.log("✅ API Service: All auth data cleared");
  }

  // Company Methods
  async createCompany(companyData: Partial<Company>): Promise<Company> {
    const response = await this.api.post<Company>("/companies", companyData);
    return response.data;
  }

  async getCompany(companyId: string): Promise<Company> {
    const response = await this.api.get<Company>(`/companies/${companyId}`);
    return response.data;
  }

  async updateCompany(
    companyId: string,
    companyData: Partial<Company>,
  ): Promise<Company> {
    const response = await this.api.put<Company>(
      `/companies/${companyId}`,
      companyData,
    );
    return response.data;
  }

  // User Management Methods
  async getCompanyUsers(
    companyId: string,
    options?: {
      include_inactive?: boolean;
      status_filter?: "all" | "active" | "inactive" | "pending";
    },
  ): Promise<User[]> {
    const params = new URLSearchParams();
    if (options?.include_inactive) {
      params.append("include_inactive", "true");
    }
    if (options?.status_filter) {
      params.append("status_filter", options.status_filter);
    }

    const url = params.toString()
      ? `/companies/${companyId}/users?${params.toString()}`
      : `/companies/${companyId}/users`;

    const response = await this.api.get<User[]>(url);
    return response.data;
  }

  async inviteUser(companyId: string, userData: Partial<User>): Promise<User> {
    const response = await this.api.post<User>(
      `/companies/${companyId}/users/invite`,
      userData,
    );
    return response.data;
  }

  async updateUser(userId: string, userData: Partial<User>): Promise<User> {
    // Only send fields that the backend UserUpdate schema expects
    const allowedFields = [
      "first_name",
      "last_name",
      "phone",
      "role",
      "is_active",
      "reports_to_id",
    ];
    const filteredData: any = {};

    for (const field of allowedFields) {
      if (userData.hasOwnProperty(field)) {
        const value = (userData as any)[field];
        // Convert role enum to string if present
        if (field === "role" && value) {
          filteredData[field] = value.toString();
        } else {
          filteredData[field] = value;
        }
      }
    }

    console.log("Sending user update data:", filteredData); // Debug log

    // Get current user to determine the correct endpoint
    const currentUser = this.getCurrentUserFromStorage();

    if (currentUser && currentUser.company_id) {
      // Company user - use company endpoint
      const response = await this.api.put<User>(
        `/companies/${currentUser.company_id}/users/${userId}`,
        filteredData,
      );
      return response.data;
    } else {
      // Legacy user - use legacy endpoint
      const response = await this.api.put<User>(
        `/users/${userId}`,
        filteredData,
      );
      return response.data;
    }
  }

  async deactivateUser(userId: string): Promise<void> {
    // Get current user to determine the correct endpoint
    const currentUser = this.getCurrentUserFromStorage();

    if (currentUser && currentUser.company_id) {
      // Company user - use company endpoint
      await this.api.delete(
        `/companies/${currentUser.company_id}/users/${userId}`,
      );
    } else {
      // Legacy user - use legacy endpoint
      await this.api.delete(`/users/${userId}`);
    }
  }

  async activateUser(userId: string): Promise<void> {
    // Get current user to determine the correct endpoint
    const currentUser = this.getCurrentUserFromStorage();

    if (currentUser && currentUser.company_id) {
      // Company user - use company endpoint
      await this.api.post(
        `/companies/${currentUser.company_id}/users/${userId}/activate`,
      );
    } else {
      // Legacy user - use legacy endpoint (update is_active to true)
      await this.updateUser(userId, { is_active: true });
    }
  }

  async resendInvitation(companyId: string, userId: string): Promise<void> {
    await this.api.post(
      `/companies/${companyId}/users/${userId}/resend-invitation`,
    );
  }

  // Candidate Methods
  async searchCandidates(
    searchRequest: CandidateSearchRequest,
  ): Promise<CandidateSearchResponse> {
    const { track_search, ...requestBody } = searchRequest;
    const params = track_search ? { track_search: "true" } : {};
    const response = await this.api.post<CandidateSearchResponse>(
      "/candidates/search",
      requestBody,
      { params },
    );
    return response.data;
  }

  async getCandidate(candidateId: string): Promise<Candidate> {
    const response = await this.api.get<Candidate>(
      `/candidates/${candidateId}`,
    );
    return response.data;
  }

  async trackCandidateProfileView(candidateId: string): Promise<any> {
    const response = await this.api.post(
      `/candidate/track/profie-view?candidate_id=${encodeURIComponent(
        candidateId,
      )}`,
    );
    return response.data;
  }

  async createCandidate(candidateData: Partial<Candidate>): Promise<Candidate> {
    const response = await this.api.post<Candidate>(
      "/candidates",
      candidateData,
    );
    return response.data;
  }

  async updateCandidate(
    candidateId: string,
    candidateData: Partial<Candidate>,
  ): Promise<Candidate> {
    const response = await this.api.put<Candidate>(
      `/candidates/${candidateId}`,
      candidateData,
    );
    return response.data;
  }

  async deleteCandidate(candidateId: string): Promise<void> {
    await this.api.delete(`/candidates/${candidateId}`);
  }

  async contactCandidate(
    candidateId: string,
    contactData: { subject: string; message: string },
  ): Promise<any> {
    const response = await this.api.post(
      `/candidates/${candidateId}/contact`,
      contactData,
    );
    return response.data;
  }

  // Smart Job Description Search
  async searchByJobDescription(
    jobSearch: JobDescriptionSearch,
  ): Promise<CandidateSearchResponse> {
    const response = await this.api.post<CandidateSearchResponse>(
      "/candidates/search-by-job",
      jobSearch,
    );
    return response.data;
  }

  // Resume Upload Methods (Updated for S3)
  async uploadResume(
    file: File,
    candidateEmail?: string,
  ): Promise<FileUploadResponse> {
    const formData = new FormData();
    formData.append("file", file);
    if (candidateEmail) {
      formData.append("candidate_email", candidateEmail);
    }
    formData.append("auto_create_candidate", "true");
    formData.append("ai_provider", "openai");

    const response = await this.api.post(
      "/resume-parsing/upload-and-parse",
      formData,
      {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      },
    );
    return response.data;
  }

  // Legacy method for resume parsing (if still needed)
  async uploadAndParseResume(
    file: File,
    candidateEmail?: string,
  ): Promise<FileUploadResponse> {
    const formData = new FormData();
    formData.append("file", file);
    if (candidateEmail) {
      formData.append("candidate_email", candidateEmail);
    }
    formData.append("auto_create_candidate", "true");

    const response = await this.api.post(
      "/resume-parsing/upload-and-parse",
      formData,
      {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      },
    );
    return response.data;
  }

  async getRecentResumes(
    limit: number = 20,
    search: string = "",
  ): Promise<any[]> {
    const params = new URLSearchParams();
    params.append("limit", limit.toString());
    if (search.trim()) {
      params.append("search", search.trim());
    }
    const response = await this.api.get(
      `/candidates/recent-resumes?${params.toString()}`,
    );
    return response.data;
  }

  async bulkUploadResumes(files: File[]): Promise<FileUploadResponse[]> {
    // For S3, we upload files individually since there's no bulk S3 endpoint yet
    const results: FileUploadResponse[] = [];

    for (const file of files) {
      try {
        const result = await this.uploadResume(file);
        results.push(result);
      } catch (error: any) {
        results.push({
          resume_id: "",
          status: ResumeStatus.FAILED,
          processing_time: 0,
          message: error.message || "Upload failed",
        });
      }
    }

    return results;
  }

  // Legacy bulk upload for resume parsing (if still needed)
  async bulkUploadAndParseResumes(
    files: File[],
  ): Promise<FileUploadResponse[]> {
    const formData = new FormData();
    files.forEach((file, index) => {
      formData.append(`files`, file);
    });
    formData.append("auto_create_candidate", "true");

    const response = await this.api.post<FileUploadResponse[]>(
      "/resume-parsing/bulk-upload-and-parse",
      formData,
      {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      },
    );
    return response.data;
  }

  async getResumeStatus(resumeId: string): Promise<Resume> {
    const response = await this.api.get<Resume>(
      `/resume-parsing/status/${resumeId}`,
    );
    return response.data;
  }

  async getResumeData(resumeId: string): Promise<Candidate> {
    const response = await this.api.get<Candidate>(
      `/resume-parsing/parsed-data/${resumeId}`,
    );
    return response.data;
  }

  async reprocessResume(
    resumeId: string,
    aiProvider: string = "openai",
  ): Promise<any> {
    const response = await this.api.post(
      `/resume-parsing/reprocess/${resumeId}`,
      {
        ai_provider: aiProvider,
        force_reprocess: true,
      },
    );
    return response.data;
  }

  async deleteResume(resumeId: string): Promise<any> {
    const response = await this.api.delete(
      `/resume-parsing/resume/${resumeId}?delete_file=true`,
    );
    return response.data;
  }

  async cleanupOrphanedCandidates(): Promise<any> {
    const response = await this.api.delete(
      "/resume-parsing/cleanup/orphaned-candidates",
    );
    return response.data;
  }

  // Analytics Methods
  async getCandidateAnalytics(): Promise<CandidateAnalytics> {
    try {
      const response = await this.api.get("/candidates/stats/overview");
      const backendData = response.data;

      // Transform backend response to frontend format
      const analytics: CandidateAnalytics = {
        total_candidates: backendData.total_candidates || 0,
        new_candidates_this_month: backendData.candidates_this_month || 0,
        top_skills: (backendData.top_skills || []).map((skill: any) => ({
          skill_name: skill.skill_name,
          candidate_count: skill.candidate_count,
          avg_proficiency: skill.avg_proficiency || 0,
        })),
        experience_distribution: backendData.experience_distribution || [],
        location_distribution: (backendData.top_locations || []).map(
          (loc: any) => ({
            location: loc.location,
            candidate_count: loc.count,
            percentage:
              backendData.total_candidates > 0
                ? (loc.count / backendData.total_candidates) * 100
                : 0,
          }),
        ),
        sector_distribution: backendData.sector_distribution || [],
        ai_processing_stats: {
          total_resumes_processed: backendData.processed_resumes || 0,
          avg_processing_time: backendData.avg_processing_time || 0,
          avg_confidence_score: backendData.avg_confidence_score || 0,
          total_cost: backendData.total_ai_cost || 0,
          success_rate: backendData.processing_success_rate || 0,
          total_resumes: backendData.total_resumes || 0,
          processing_resumes: backendData.processing_resumes || 0,
          failed_resumes: backendData.failed_resumes || 0,
        },
        search_analytics: {
          total_searches: backendData.total_searches || 0,
          avg_results_per_search: backendData.avg_results_per_search || 0,
          top_search_terms: backendData.top_search_terms || [],
          search_trends: backendData.search_trends || [],
        },
        // Comprehensive data analytics
        top_certifications: backendData.top_certifications || [],
        top_languages: backendData.top_languages || [],
        top_project_technologies: backendData.top_project_technologies || [],
        awards_distribution: backendData.awards_distribution || [],
        candidates_with_projects: backendData.candidates_with_projects || 0,
        candidates_with_certifications:
          backendData.candidates_with_certifications || 0,
        candidates_with_awards: backendData.candidates_with_awards || 0,
        candidates_with_languages: backendData.candidates_with_languages || 0,
        avg_projects_per_candidate: backendData.avg_projects_per_candidate || 0,
        avg_certifications_per_candidate:
          backendData.avg_certifications_per_candidate || 0,
      };

      return analytics;
    } catch (error: any) {
      console.error("Failed to fetch candidate analytics:", error);
      throw new Error(
        `Failed to load analytics: ${
          error.response?.data?.detail || error.message
        }`,
      );
    }
  }

  async getCandidateProfileAnalytics(
    candidateId: string,
  ): Promise<CandidateProfileAnalytics> {
    const response = await this.api.get<CandidateProfileAnalytics>(
      `/candidates/${candidateId}/analytics`,
    );
    return response.data;
  }

  // Super Admin Methods
  async getSuperAdminStats(): Promise<SuperAdminStats> {
    const response = await this.api.get<SuperAdminStats>("/super-admin/stats");
    return response.data;
  }

  async getAllCompanies(): Promise<Company[]> {
    const response = await this.api.get<Company[]>("/super-admin/companies");
    return response.data;
  }

  async getAllUsers(): Promise<SuperAdminUsersResponse> {
    const response =
      await this.api.get<SuperAdminUsersResponse>("/super-admin/users");
    return response.data;
  }

  async updateCompanySubscription(
    companyId: string,
    subscriptionData: any,
  ): Promise<Company> {
    const response = await this.api.put<Company>(
      `/super-admin/companies/${companyId}/subscription`,
      subscriptionData,
    );
    return response.data;
  }

  async assignSalesContact(
    companyId: string,
    salesContactId: string,
  ): Promise<Company> {
    const response = await this.api.put<Company>(
      `/super-admin/companies/${companyId}/sales-contact`,
      { sales_contact_id: salesContactId },
    );
    return response.data;
  }

  async createAdminBillingTier(tierData: any): Promise<any> {
    const response = await this.api.post("/admin/billing/tiers", tierData);
    return response.data;
  }

  async getAdminBillingTier(tierId: string): Promise<any> {
    const response = await this.api.get(`/admin/billing/tiers/${tierId}`);
    return response.data;
  }

  async updateAdminBillingTier(tierId: string, tierData: any): Promise<any> {
    const response = await this.api.put(`/admin/billing/tiers/${tierId}`, tierData);
    return response.data;
  }

  async getAdminBillingTierVersions(tierId: string): Promise<any> {
    const response = await this.api.get(`/admin/billing/tiers/${tierId}/versions`);
    return response.data;
  }

  async updateAdminBillingTierVersion(versionId: string, versionData: any): Promise<any> {
    const response = await this.api.put(`/admin/billing/tier-versions/${versionId}`, versionData);
    return response.data;
  }

  async createAdminBillingTierVersion(tierId: string, versionData: any): Promise<any> {
    const response = await this.api.post(`/admin/billing/tiers/${tierId}/versions`, versionData);
    return response.data;
  }

  async deleteAdminBillingTierVersion(versionId: string): Promise<any> {
    const response = await this.api.delete(`/admin/billing/tier-versions/${versionId}`);
    return response.data;
  }

  async getAdminTrialConfig(): Promise<AdminTrialConfig> {
    const response = await this.api.get<AdminTrialConfig>("/admin/billing/trial-config");
    return response.data;
  }

  async updateAdminTrialConfig(
    trialConfigData: AdminTrialConfigUpdateRequest,
  ): Promise<AdminTrialConfig> {
    const response = await this.api.put<AdminTrialConfig>(
      "/admin/billing/trial-config",
      null,
      { params: trialConfigData },
    );
    return response.data;
  }

  async getAdminCreditActions(params?: AdminCreditActionsQuery): Promise<any> {
    const response = await this.api.get("/admin/billing/credit-actions", {
      params,
    });
    return response.data;
  }

  async getAdminCreditActionsByTier(
    tierId?: string,
    params?: AdminCreditActionsQuery,
  ): Promise<any> {
    const response = await this.api.get("/admin/billing/credit-actions/by-tier", {
      params: {
        ...params,
        ...(tierId ? { tier_id: tierId } : {}),
      },
    });
    return response.data;
  }

  async updateAdminCreditActionCosts(
    tierId: string,
    actionCosts: Record<string, number>,
  ): Promise<any> {
    const response = await this.api.put(`/admin/billing/credit-actions/${tierId}`, {
      action_costs: actionCosts,
    });
    return response.data;
  }

  async getAdminCreditActionCodes(): Promise<any> {
    const response = await this.api.get("/admin/billing/credit-action-code");
    return response.data;
  }

  async createAdminCreditActionType(actionTypeData: any): Promise<any> {
    const response = await this.api.post(
      "/admin/billing/credit-action-type",
      actionTypeData,
    );
    return response.data;
  }

  async updateAdminCreditActionType(actionTypeData: any): Promise<any> {
    const response = await this.api.put(
      "/admin/billing/credit-action-type",
      actionTypeData,
    );
    return response.data;
  }

  async updateAdminCompanyStatus(
    companyId: string,
    active: boolean,
  ): Promise<{ detail: string }> {
    const params = new URLSearchParams({
      company_id: companyId,
      active: String(active),
    });
    const response = await this.api.put<{ detail: string }>(
      `/super-admin/companies/update-status?${params.toString()}`,
    );
    return response.data;
  }

  async deactivateAdminCompany(companyId: string): Promise<{ detail: string }> {
    return this.updateAdminCompanyStatus(companyId, false);
  }

  // File Management Methods
  async getStorageStats(): Promise<any> {
    const response = await this.api.get("/file-management/storage-stats");
    return response.data;
  }

  async cleanupOldFiles(olderThanHours: number): Promise<any> {
    const response = await this.api.post("/file-management/cleanup-old-files", {
      older_than_hours: olderThanHours,
    });
    return response.data;
  }

  // S3 Storage Management Methods
  async getStorageInfo(): Promise<any> {
    const response = await this.api.get("/storage/info");
    return response.data;
  }

  async getStoragePlans(): Promise<any> {
    const response = await this.api.get("/storage/plans");
    return response.data;
  }

  async upgradeStoragePlan(planId: string): Promise<any> {
    const response = await this.api.post("/storage/upgrade", {
      plan_id: planId,
    });
    return response.data;
  }

  async getStorageRecommendations(): Promise<any> {
    const response = await this.api.get("/storage/recommendations");
    return response.data;
  }

  async getStorageUsageAlert(): Promise<any> {
    const response = await this.api.get("/storage/usage-alert");
    return response.data;
  }

  async listFiles(
    fileType: string,
    page: number = 1,
    limit: number = 50,
  ): Promise<any> {
    const response = await this.api.get(
      `/files/list/${fileType}?page=${page}&limit=${limit}`,
    );
    return response.data;
  }

  async downloadFile(fileType: string, fileId: string): Promise<Blob> {
    const response = await this.api.get(
      `/files/download/${fileType}/${fileId}`,
      {
        responseType: "blob",
      },
    );
    return response.data;
  }

  async deleteFile(fileType: string, fileId: string): Promise<any> {
    const response = await this.api.delete(
      `/files/delete/${fileType}/${fileId}`,
    );
    return response.data;
  }

  async getFilePreview(fileType: string, fileId: string): Promise<any> {
    const response = await this.api.get(`/files/preview/${fileType}/${fileId}`);
    return response.data;
  }

  // Utility Methods
  isAuthenticated(): boolean {
    return !!localStorage.getItem("access_token");
  }

  getCurrentUserFromStorage(): User | null {
    const userStr = localStorage.getItem("user");
    return userStr ? JSON.parse(userStr) : null;
  }

  // Job Management Methods
  async getJobs(companyId: string): Promise<Job[]> {
    const response = await this.api.get<Job[]>(`/companies/${companyId}/jobs`);
    return response.data;
  }

  async createJob(companyId: string, jobData: Partial<Job>): Promise<Job> {
    const response = await this.api.post<Job>(
      `/companies/${companyId}/jobs`,
      jobData,
    );
    return response.data;
  }

  async updateJob(jobId: string, jobData: Partial<Job>): Promise<Job> {
    const response = await this.api.put<Job>(`/jobs/${jobId}`, jobData);
    return response.data;
  }

  async deleteJob(jobId: string): Promise<void> {
    await this.api.delete(`/jobs/${jobId}`);
  }

  // Data cleanup method for testing
  async cleanupAllData(): Promise<any> {
    const response = await this.api.delete("/candidates/cleanup/all");
    return response.data;
  }

  // Get skills suggestions for autocomplete
  async getSkillsSuggestions(query: string = "", limit: number = 10) {
    const response = await this.api.get(
      `/candidates/suggestions/skills?query=${encodeURIComponent(
        query,
      )}&limit=${limit}`,
    );
    return response.data;
  }

  // Get location suggestions for autocomplete
  async getLocationsSuggestions(query: string = "", limit: number = 10) {
    const response = await this.api.get(
      `/candidates/suggestions/locations?query=${encodeURIComponent(
        query,
      )}&limit=${limit}`,
    );
    return response.data;
  }

  // Candidate Profile Methods (for candidates themselves)
  async checkCandidateProfileExists(): Promise<any> {
    const response = await this.api.get("/candidate/profile/exists");
    return response.data;
  }

  async createCandidateProfileFromResume(file: File): Promise<any> {
    const formData = new FormData();
    formData.append("resume_file", file);

    const response = await this.api.post(
      "/candidate/profile/create-from-resume",
      formData,
      {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      },
    );
    return response.data;
  }

  async getCandidateProfile(): Promise<Candidate> {
    const response = await this.api.get<Candidate>("/candidate/profile");
    return response.data;
  }

  async updateCandidateProfile(profileData: any): Promise<any> {
    const response = await this.api.put("/candidate/profile", profileData);
    return response.data;
  }

  async deleteCandidateProfile(): Promise<any> {
    const response = await this.api.delete("/candidate/delete-job-seeker");
    return response.data;
  }

  async getMyCandidateAnalytics(): Promise<any> {
    const response = await this.api.get("/candidate/profile/analytics");
    return response.data;
  }

  async getMyCandidateAnalyticsDetailed(): Promise<any> {
    const response = await this.api.get("/candidate/profile/analytics");
    return response.data;
  }

  async getCandidateNotifications(
    skip: number = 0,
    limit: number = 20,
    unreadOnly: boolean = false,
  ): Promise<any> {
    const response = await this.api.get("/candidate/notifications", {
      params: { skip, limit, unread_only: unreadOnly },
    });
    return response.data;
  }

  async markNotificationRead(notificationId: string): Promise<any> {
    const response = await this.api.post(
      `/candidate/notifications/${notificationId}/mark-read`,
    );
    return response.data;
  }

  async markNotificationUnread(notificationId: string): Promise<any> {
    const response = await this.api.post(
      `/candidate/notifications/${notificationId}/mark-unread`,
    );
    return response.data;
  }

  async markAllNotificationsRead(): Promise<any> {
    const response = await this.api.post(
      "/candidate/notifications/mark-all-read",
    );
    return response.data;
  }

  async trackSearchAppearance(
    candidateIds: string[],
    searchQuery: string | Record<string, unknown> = "",
  ): Promise<any> {
    const response = await this.api.post(
      "/candidate/track/search-appearance",
      candidateIds,
      {
        params: {
          search_query:
            typeof searchQuery === "string"
              ? searchQuery
              : JSON.stringify(searchQuery),
        },
      },
    );
    return response.data;
  }

  async startVerification(
    verificationType:
      | "id_verification"
      | "background_check"
      | "skill_assessment",
  ): Promise<any> {
    const response = await this.api.post(
      `/candidate/profile/verification/${verificationType}`,
    );
    return response.data;
  }

  async replyToRecruiterMessage(
    notificationId: string,
    message: string,
  ): Promise<any> {
    const response = await this.api.post(
      `/candidate/notifications/${notificationId}/reply`,
      {
        message,
      },
    );
    return response.data;
  }

  async loginJobSeeker(credentials: LoginRequest): Promise<TokenResponse> {
    // Check if this is a super admin email (TalentKey.AI domain)
    if (credentials.email.toLowerCase().includes("@talentkeyai.com")) {
      try {
        console.log(
          "🔧 Trying super admin login for job seeker flow:",
          credentials.email,
        );
        const response = await this.api.post<TokenResponse>(
          "/auth/super-admin/login",
          credentials,
        );
        const { access_token, refresh_token, user } = response.data;
        const userWithPersona = user
          ? { ...user, persona: response.data.persona || user.persona }
          : user;

        localStorage.setItem("access_token", access_token);
        localStorage.setItem("refresh_token", refresh_token);
        if (userWithPersona) {
          localStorage.setItem("user", JSON.stringify(userWithPersona));
        }

        console.log("✅ Super admin login successful via job seeker flow");
        return response.data;
      } catch (superAdminError: any) {
        console.log(
          "❌ Super admin login failed in job seeker flow, trying regular job seeker login",
        );
        // Fall through to regular job seeker login
      }
    }

    const response = await this.api.post<TokenResponse>(
      "/auth/job-seeker/login",
      credentials,
    );
    const { access_token, refresh_token } = response.data;

    localStorage.setItem("access_token", access_token);
    localStorage.setItem("refresh_token", refresh_token);

    // Store user data from response
    if (response.data.user) {
      const userWithPersona = {
        ...response.data.user,
        persona: response.data.persona || response.data.user.persona,
      };
      localStorage.setItem("user", JSON.stringify(userWithPersona));
    }

    return response.data;
  }

  async loginCompanyUser(credentials: LoginRequest): Promise<TokenResponse> {
    // Check if this is a super admin email (TalentKey.AI domain)
    if (credentials.email.toLowerCase().includes("@talentkeyai.com")) {
      try {
        console.log(
          "🔧 Trying super admin login for company user flow:",
          credentials.email,
        );
        const response = await this.api.post<TokenResponse>(
          "/auth/super-admin/login",
          credentials,
        );
        const { access_token, refresh_token, user } = response.data;
        const userWithPersona = user
          ? { ...user, persona: response.data.persona || user.persona }
          : user;

        localStorage.setItem("access_token", access_token);
        localStorage.setItem("refresh_token", refresh_token);
        if (userWithPersona) {
          localStorage.setItem("user", JSON.stringify(userWithPersona));
        }

        console.log("✅ Super admin login successful via company user flow");
        return response.data;
      } catch (superAdminError: any) {
        console.log(
          "❌ Super admin login failed in company user flow, trying regular company user login",
        );
        // Fall through to regular company user login
      }
    }

    const response = await this.api.post<TokenResponse>(
      "/auth/company-user/login",
      credentials,
    );
    const { access_token, refresh_token } = response.data;

    localStorage.setItem("access_token", access_token);
    localStorage.setItem("refresh_token", refresh_token);

    // Store user data from response
    if (response.data.user) {
      const userWithPersona = {
        ...response.data.user,
        persona: response.data.persona || response.data.user.persona,
      };
      localStorage.setItem("user", JSON.stringify(userWithPersona));
    }

    return response.data;
  }

  async getCandidateProfileCreationStatus(taskId: string): Promise<any> {
    const response = await this.api.get(
      `/candidate/profile/creation-status/${taskId}`,
    );
    return response.data;
  }

  // OTP Verification Methods
  async sendVerificationOTP(
    email: string,
    firstName: string = "User",
  ): Promise<{ message: string; email: string }> {
    const response = await this.api.post("/auth/send-verification-otp", {
      email,
      first_name: firstName,
    });
    return response.data;
  }

  async verifyEmailOTP(
    email: string,
    otpCode: string,
  ): Promise<{ message: string; verified: boolean }> {
    const response = await this.api.post("/auth/verify-email-otp", {
      email,
      otp_code: otpCode,
    });
    return response.data;
  }

  async verifyJobSeekerEmailOTP(
    email: string,
    otpCode: string,
  ): Promise<{ message: string; verified: boolean; next_step: string }> {
    const response = await this.api.post("/auth/job-seeker/verify-email-otp", {
      email,
      otp_code: otpCode,
    });
    return response.data;
  }

  async verifyCompanyUserEmailOTP(
    email: string,
    otpCode: string,
  ): Promise<{ message: string; verified: boolean; next_step: string }> {
    const response = await this.api.post(
      "/auth/company-user/verify-email-otp",
      {
        email,
        otp_code: otpCode,
      },
    );
    return response.data;
  }

  async completeJobSeekerSetup(
    email: string,
    currentPassword: string,
    newPassword: string,
  ): Promise<{ message: string; setup_completed: boolean }> {
    const response = await this.api.post("/auth/job-seeker/complete-setup", {
      email,
      current_password: currentPassword,
      new_password: newPassword,
    });
    return response.data;
  }

  async changeJobSeekerPassword(
    currentPassword: string,
    newPassword: string,
    confirmPassword: string,
  ): Promise<{ message: string }> {
    const response = await this.api.post("/auth/job-seeker/change-password", {
      current_password: currentPassword,
      new_password: newPassword,
      confirm_password: confirmPassword,
    });
    return response.data;
  }

  async changeCompanyUserPassword(
    currentPassword: string,
    newPassword: string,
    confirmPassword: string,
  ): Promise<{ message: string }> {
    const response = await this.api.post("/auth/companies/change-password", {
      current_password: currentPassword,
      new_password: newPassword,
      confirm_password: confirmPassword,
    });
    return response.data;
  }

  async completeCompanyUserSetup(
    email: string,
    currentPassword: string,
    newPassword: string,
  ): Promise<{ message: string; setup_completed: boolean }> {
    const response = await this.api.post("/auth/company-user/complete-setup", {
      email,
      current_password: currentPassword,
      new_password: newPassword,
    });
    return response.data;
  }

  async resendVerificationOTP(email: string): Promise<{ message: string }> {
    const response = await this.api.post("/auth/resend-verification-otp", {
      email,
    });
    return response.data;
  }

  // Public Profile Management
  async generatePublicProfileLink(): Promise<any> {
    const response = await this.api.post(
      "/candidate/profile/generate-public-link",
      {},
    );
    return response.data;
  }

  async updatePublicProfileSettings(settings: any): Promise<any> {
    const response = await this.api.put(
      "/candidate/profile/public-settings",
      settings,
    );
    return response.data;
  }

  async getPublicProfile(slug: string): Promise<any> {
    const response = await this.api.get(`/candidate/profile/public/${slug}`);
    return response.data;
  }

  // Resume Template Generation
  async generateResumeTemplate(
    templateOptions?: any,
  ): Promise<{ success: boolean; filename: string }> {
    const response = await this.api.post(
      "/candidate/resume/generate-template",
      templateOptions || {},
      {
        responseType: "blob",
      },
    );

    // Extract filename from Content-Disposition header
    const contentDisposition = response.headers["content-disposition"];
    let filename = "resume.pdf";

    if (contentDisposition) {
      const filenameMatch = contentDisposition.match(
        /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/,
      );
      if (filenameMatch && filenameMatch[1]) {
        filename = filenameMatch[1].replace(/['"]/g, "");
      }
    }

    // Create blob URL and trigger download
    const blob = new Blob([response.data], { type: "application/pdf" });
    const url = window.URL.createObjectURL(blob);

    // Create temporary link and trigger download
    const link = document.createElement("a");
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();

    // Cleanup
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);

    return {
      success: true,
      filename: filename,
    };
  }

  async getAvailableResumeTemplates(): Promise<any> {
    const response = await this.api.get("/candidate/resume/templates");
    return response.data;
  }

  async completeSetup(
    email: string,
    currentPassword: string,
    newPassword: string,
  ): Promise<void> {
    await this.api.post("/auth/company-user/complete-setup", {
      email,
      current_password: currentPassword,
      new_password: newPassword,
    });
  }

  // Location API Methods
  async getCountries(): Promise<any[]> {
    const response = await this.api.get("/locations/countries");
    return response.data;
  }

  async getStates(countryCode: string): Promise<any[]> {
    const response = await this.api.get(`/locations/states/${countryCode}`);
    return response.data;
  }

  async getCities(countryCode: string, stateCode: string): Promise<any[]> {
    const response = await this.api.get(
      `/locations/cities/${countryCode}/${stateCode}`,
    );
    return response.data;
  }

  async getCitiesForCountry(countryCode: string): Promise<any[]> {
    const response = await this.api.get(`/locations/cities/${countryCode}`);
    return response.data;
  }

  // Conversations and Messaging
  async getConversations(
    status: string = "active",
    viewType: string = "my_conversations",
    limit: number = 20,
    offset: number = 0,
    search?: string,
    priority?: string,
    accountStatus?: string,
    dateFilter?: string,
  ): Promise<any> {
    const params = new URLSearchParams();

  params.append("status", status);
  params.append("view_type", viewType);
  params.append("limit", limit.toString());
  params.append("offset", offset.toString());

  if (search) params.append("search", search);
  if (priority && priority !== "all")
    params.append("priority", priority);
  if (accountStatus && accountStatus !== "all")
    params.append("account_status", accountStatus);
  if (dateFilter && dateFilter !== "all")
    params.append("date_filter", dateFilter);
    const response = await this.api.get(
      `/conversations?status=${status}&view_type=${viewType}&limit=${limit}&offset=${offset}`,
    );
    return response.data;
  }

  async getConversationMessages(
    conversationId: string,
    limit: number = 50,
    offset: number = 0,
  ): Promise<any> {
    const response = await this.api.get(
      `/conversations/${conversationId}/messages?limit=${limit}&offset=${offset}`,
    );
    return response.data;
  }

  async sendMessageToConversation(
    conversationId: string,
    messageData: {
      subject: string;
      content: string;
      message_type?: string;
      job_id?: string;
    },
  ): Promise<any> {
    const response = await this.api.post(
      `/conversations/${conversationId}/messages`,
      messageData,
    );
    return response.data;
  }

  async updateConversationStatus(
    conversationId: string,
    status: string,
  ): Promise<any> {
    const response = await this.api.patch(
      `/conversations/${conversationId}/status`,
      { status },
    );
    return response.data;
  }

  async updateConversationPriority(
    conversationId: string,
    priority: string,
  ): Promise<any> {
    const response = await this.api.patch(
      `/conversations/${conversationId}/priority`,
      { priority },
    );
    return response.data;
  }

  // Candidate Messages (Job Seeker) Methods
  async getCandidateMessages(
    status: string = "active",
    limit: number = 20,
    offset: number = 0,
  ): Promise<any> {
    const response = await this.api.get(`/candidate/messages`, {
      params: { status, limit, offset },
    });
    return response.data;
  }

  async getCandidateConversationMessages(
    conversationId: string,
    limit: number = 50,
    offset: number = 0,
  ): Promise<any> {
    const response = await this.api.get(
      `/candidate/messages/${conversationId}`,
      {
        params: { limit, offset },
      },
    );
    return response.data;
  }

  async replyCandidateToConversation(
    conversationId: string,
    replyData: { content: string; in_reply_to_id?: string },
  ): Promise<any> {
    const response = await this.api.post(
      `/candidate/messages/${conversationId}/reply`,
      replyData,
    );
    return response.data;
  }

  async updateCandidateConversationStatus(
    conversationId: string,
    status: "active" | "archived" | "closed",
  ): Promise<any> {
    const response = await this.api.patch(
      `/candidate/messages/${conversationId}/status`,
      { status },
    );
    return response.data;
  }
}

export const apiService = new ApiService();
export default apiService;
